import React from 'react';
import { Card, DatePicker, Row, Col, Spin, Typography, Empty } from 'antd';
import { Line, Column, Area } from '@ant-design/charts';
import dayjs from 'dayjs';
import { useAnalytics } from '@/hooks/useAnalytics';

const { RangePicker } = DatePicker;
const { Title } = Typography;

const AnalyticsPage: React.FC = () => {
  const { userGrowth, dau, imageUploads, loading, dateRange, setDateRange } =
    useAnalytics();

  const handleRangeChange = (
    dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null,
  ) => {
    if (dates && dates[0] && dates[1]) {
      setDateRange([dates[0], dates[1]]);
    }
  };

  const commonAxisConfig = {
    xAxis: {
      tickCount: 8,
      label: {
        formatter: (text: string) => dayjs(text).format('MM/DD'),
      },
    },
  };

  const userGrowthConfig = {
    data: userGrowth,
    xField: 'date',
    yField: 'count',
    smooth: true,
    color: '#1677ff',
    height: 320,
    point: { size: 2, shape: 'circle' as const },
    ...commonAxisConfig,
  };

  const dauConfig = {
    data: dau,
    xField: 'date',
    yField: 'count',
    color: '#52c41a',
    height: 320,
    columnWidthRatio: 0.5,
    ...commonAxisConfig,
  };

  const imageConfig = {
    data: imageUploads,
    xField: 'date',
    yField: 'count',
    smooth: true,
    color: '#722ed1',
    height: 320,
    areaStyle: { fillOpacity: 0.15 },
    ...commonAxisConfig,
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
          flexWrap: 'wrap',
          gap: 16,
        }}
      >
        <Title level={4} style={{ margin: 0 }}>
          Analytics
        </Title>
        <RangePicker
          value={dateRange}
          onChange={handleRangeChange}
          allowClear={false}
          disabledDate={(current) => current && current > dayjs().endOf('day')}
        />
      </div>

      <Row gutter={[24, 24]}>
        <Col xs={24}>
          <Card title="User Growth">
            <Spin spinning={loading}>
              {userGrowth.length > 0 ? (
                <Line {...userGrowthConfig} />
              ) : (
                <Empty description="No data available" />
              )}
            </Spin>
          </Card>
        </Col>

        <Col xs={24}>
          <Card title="Daily Active Users">
            <Spin spinning={loading}>
              {dau.length > 0 ? (
                <Column {...dauConfig} />
              ) : (
                <Empty description="No data available" />
              )}
            </Spin>
          </Card>
        </Col>

        <Col xs={24}>
          <Card title="Image Uploads">
            <Spin spinning={loading}>
              {imageUploads.length > 0 ? (
                <Area {...imageConfig} />
              ) : (
                <Empty description="No data available" />
              )}
            </Spin>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default AnalyticsPage;
